import type { TovVersionResponse } from "contracts/api/client-tov/version.responses";
import { createSignal, For } from "solid-js";
import { api } from "../../store/api";
import { createAsyncAction } from "../../ui/asyncAction";
import { FormError } from "../../ui/FormError";
import { createImportableVoices } from "./playgroundQueries";

/**
 * Reuse an existing Client's processed voice: pick a real Client and deep-copy
 * its active voice into this workspace. The list comes from the public
 * client_tov_profiles ⋈ clients join, so it needs no per-Client schema access.
 */
export function VoiceImportPicker(props: {
  clientId: string;
  onImported: (version: TovVersionResponse) => void;
}) {
  const voices = createImportableVoices();
  const [sourceId, setSourceId] = createSignal("");
  const action = createAsyncAction();

  const importVoice = () => {
    if (!sourceId() || action.busy()) return;
    void action.run(async () => {
      const version = await api.playground.importVoice(
        props.clientId,
        sourceId(),
      );
      setSourceId("");
      props.onImported(version);
    });
  };

  return (
    <section class="tov-import">
      <div class="field">
        <span class="field-label">Reuse a client's voice</span>
        <div class="tov-import-row">
          <select
            class="editor-input"
            value={sourceId()}
            onChange={(event) => setSourceId(event.currentTarget.value)}
          >
            <option value="">Pick a client…</option>
            <For each={voices.data?.voices ?? []}>
              {(voice) => (
                <option value={voice.clientId}>
                  {voice.clientName} — {voice.profileName}
                </option>
              )}
            </For>
          </select>
          <button
            class="btn btn-primary"
            type="button"
            disabled={!sourceId() || action.busy()}
            onClick={importVoice}
          >
            {action.busy() ? "Importing…" : "Import voice"}
          </button>
        </div>
        <span class="field-hint">
          Copies the client's active voice into this workspace as an editable
          version. It won't change when the source client edits theirs.
        </span>
        <FormError message={action.error()} />
      </div>
    </section>
  );
}
